// CONTRACT:C3-CAPABILITY-PLUGIN.1.0
/** ContributionRegistry — the host-side store of every contribution a capability
 *  makes (layers / panels / commands / context-menu items). Each register* returns
 *  a Disposable; the registry tracks contributions per capability so the host can
 *  tear down ALL of one capability's contributions (deactivate / crash isolation)
 *  leaving no orphans (## Behavioral Contracts: "Contributions are disposable"). */

import type {
  Disposable,
  LayerSpec,
  PanelSpec,
  CommandSpec,
  ContextMenuItem,
} from "../../contracts-ts/src/index.ts";
import { ErrUnknownContribution } from "./errors.ts";

/** The contribution kinds this host supports. New kinds are additive (minor bump);
 *  anything outside this set raises ErrUnknownContribution. */
export type ContributionKind = "layer" | "panel" | "command" | "context-menu";

/** The supported set, used to validate generic register() calls. */
export const SUPPORTED_KINDS: readonly ContributionKind[] = [
  "layer",
  "panel",
  "command",
  "context-menu",
];

/** A single recorded contribution with the spec the capability supplied. */
export interface ContributionRecord {
  readonly kind: ContributionKind;
  readonly capabilityId: string;
  readonly spec: LayerSpec | PanelSpec | CommandSpec | ContextMenuItem;
}

interface InternalEntry extends ContributionRecord {
  readonly teardown: () => void;
}

export class ContributionRegistry {
  /** Per-capability list of live contribution entries, insertion-ordered. */
  private readonly byCapability = new Map<string, InternalEntry[]>();

  /** Record a contribution for a capability and return a Disposable. Disposing the
   *  returned handle removes exactly that one contribution (idempotent). */
  add(
    capabilityId: string,
    kind: ContributionKind,
    spec: ContributionRecord["spec"],
    teardown?: () => void,
  ): Disposable {
    if (!SUPPORTED_KINDS.includes(kind)) {
      // Never partially apply: refuse before recording anything.
      throw new ErrUnknownContribution(kind, capabilityId);
    }
    const entry: InternalEntry = {
      kind,
      capabilityId,
      spec,
      teardown: teardown ?? (() => {}),
    };
    const list = this.byCapability.get(capabilityId) ?? [];
    list.push(entry);
    this.byCapability.set(capabilityId, list);

    let disposed = false;
    return {
      dispose: () => {
        if (disposed) return;
        disposed = true;
        this.removeEntry(capabilityId, entry);
      },
    };
  }

  /** Live contributions for one capability (defensive copy). */
  list(capabilityId: string): ContributionRecord[] {
    return (this.byCapability.get(capabilityId) ?? []).map((e) => ({
      kind: e.kind,
      capabilityId: e.capabilityId,
      spec: e.spec,
    }));
  }

  /** Live contributions of a given kind for one capability. */
  listKind(capabilityId: string, kind: ContributionKind): ContributionRecord[] {
    return this.list(capabilityId).filter((c) => c.kind === kind);
  }

  /** Count of all live contributions across every capability. */
  size(): number {
    let n = 0;
    for (const list of this.byCapability.values()) n += list.length;
    return n;
  }

  /** Tear down EVERY contribution for one capability, leaving no orphans. Each
   *  teardown is isolated so one throwing teardown cannot block the rest. */
  disposeAll(capabilityId: string): void {
    const list = this.byCapability.get(capabilityId);
    if (!list) return;
    // Copy + clear first so re-entrant dispose() calls are no-ops.
    const entries = list.slice();
    this.byCapability.delete(capabilityId);
    for (const entry of entries) {
      try {
        entry.teardown();
      } catch {
        // A teardown throw must not prevent cleaning up the rest.
      }
    }
  }

  private removeEntry(capabilityId: string, entry: InternalEntry): void {
    const list = this.byCapability.get(capabilityId);
    if (!list) return;
    const idx = list.indexOf(entry);
    if (idx === -1) return;
    list.splice(idx, 1);
    if (list.length === 0) this.byCapability.delete(capabilityId);
    try {
      entry.teardown();
    } catch {
      // ignore teardown failures on single-dispose
    }
  }
}
